import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { isOpenerVariant, variantTellsFacts } from '@game-platform/pack-animation';
import { toast } from 'sonner';
import { useSkipAnimation } from './playerPreference';
import {
  sellPoolItem,
  type ApiClient,
  // type IframePoolItem, // used by the disabled item-card reel feed below
  type IframePoolOrder,
  type IframePoolOrderItem,
  type VolatilityLevel,
} from '../api';
import { compressedImageUrl } from '../design-system';
import { preloadWinnerImage } from './winnerImage';
import { formatPrice } from '../currency';
import { gameEvents } from '../postMessage';
import { trackBeginCheckout } from '../tracking';
import { canAffordOffer, useWallet } from '../wallet';
import { usePayments } from '../payments/usePayments';
import { notifyBuyback, notifyVaulted } from '../notifications';
import { winHaptic } from '../haptics';
import { useShippingDisabled } from '../shipping';
import { useIsSleek } from '../theme';
import { isLowBalanceLeftToHost, openDepositUrl, useOperatorLinks } from '../operatorLinks';
import { RevealShippingModal } from './RevealShippingModal';
import { CardShuffleReel } from './CardShuffleReel';
import { PACK_REEL_IMG } from './CardSlotTile';
import { RevealGlowOverlay, type GlowPhase } from './RevealGlowOverlay';
import { RevealRays } from './RevealRays';
import { BadgeRevealSequence } from './BadgeRevealSequence';
import { isRarePlus } from './glowColorFor';
import { EXPAND_DELAY_MS } from './readyStep.constants';
// Item-card reel feed — temporarily unused, see the disabled block below.
// import { useNextPackItems } from './useNextPackItems';
import { WinnerContent } from './WinnerContent';
import {
  buyAndOpenNextPack,
  nextPackErrorCopy,
  pullDemoNextPack,
  type NextPackOutcome,
} from './nextPackPurchase';
import type { DemoRevealSource } from './demoSource';
import type { SellOptions } from './useRevealMachine';
import { useOpeningAnimation } from '../openingAnimation';
import { OpenerReadyStep } from './opener/OpenerReadyStep';
import { cardFactsFor } from './opener/cardFacts';
import { pedestalSrcFor } from './pedestalSrc';
import { SLEEK_RESULT_HOLD_MS } from './sleek/SoldLine';

const INSUFFICIENT_BALANCE_MESSAGE = 'Insufficient balance. Please add funds and try again.';

export type SellState = 'idle' | 'selling' | 'sold';

export interface WinnerStepProps {
  /** API client — used by the Create Shipping modal to fetch fee figures. */
  client: ApiClient;
  item: IframePoolOrderItem;
  offer: IframePoolOrder['poolOffer'];
  sellState: SellState;
  /** Fires the buyback. The optional `afterSold` runs after the
   *  network call succeeds. The winner screen calls this WITHOUT a
   *  callback — the sale lands, `sellState` flips to `sold`, and the
   *  screen stays mounted to play the payout celebration. Navigation
   *  happens later, from "Open Next Pack". */
  onSell: (afterSold?: () => void, options?: SellOptions) => void;
  onBuyAgain: () => void;
  /** "Create Shipping" link — hands off to the host's shipping flow.
   *  Also re-used as the close path back to the pack page. */
  onShowPack: () => void;
  /** "Confirm Shipping" on the reveal modal — start the delivery flow for
   *  THIS item. Falls back to onShowPack when not provided. */
  onShip?: (item: IframePoolOrderItem) => void;
  /** While this purchase still has a sealed pack ahead, "Open Next Pack"
   *  advances to it (open an already-bought pack) instead of buying. */
  hasNextSealedPack?: boolean;
  /** Advance to the next still-sealed pack of this purchase. */
  onAdvanceToNextSealed?: () => void;
  /** Closes the reveal once this purchase has no sealed pack left: the
   *  forward CTA reads "Done" and calls this instead of buying another pack.
   *  Sleek always passes it, and also calls it straight after Sell / Vault
   *  on that last pack (partner feedback 2026-10-01). */
  onDone?: () => void;
  /** Bubbles up while this winner screen is in an Open Next Pack loop or
   *  shuffle, so the host can lock pack navigation (a remount then would
   *  destroy the in-flight loop pack). */
  onLoopActiveChange?: (active: boolean) => void;
  /** When `true`, the entire winner screen paints in its FINAL state
   *  with no entrance choreography — used for returning views. */
  instant?: boolean;
  /** Volatility the player selected on the pack page — "Open Next Pack"
   *  buys the fresh pack at this level (defaults to normal). */
  volatility?: VolatilityLevel;
  /** Where the first episode's card is (see WinnerContent): `scene` when
   *  the opening scene revealed it and stays on screen underneath. */
  cardSlot?: 'card' | 'scene';
  /** Set = free demo rip: the loop pulls another free card instead of
   *  buying one, there is no balance gate and nothing can be sold. The
   *  episode/scene/reel choreography is untouched. See `DemoRevealSource`. */
  demo?: DemoRevealSource;
}


/**
 * Winner screen orchestrator. Plays a sequence of winner "episodes":
 *
 *   episode → [Accept Buyback → payout] → [Open Next Pack → full-screen
 *   shuffle → lands on the REAL next pull] → NEXT episode (fresh reveal).
 *
 * "Open Next Pack" actually buys the next pack in place (no RevealScreen
 * remount): it fires the create → purchase → open API sequence while the
 * reel spins, then lands the reel on the real pull and opens a new episode
 * with the real card (real FMV + buyback offer, real sell). If the
 * purchase fails the reel aborts to an in-place error panel (Try Again /
 * Back) instead of landing on a card.
 *
 * `WinnerContent` owns each episode's reveal cascade and is keyed per
 * episode, so a new episode remounts it and the cascade replays.
 */
export function WinnerStep({
  client,
  item,
  offer,
  sellState,
  onSell,
  // onBuyAgain stays in the props (the host still wires it) but the
  // in-place loop buys directly via the API so it doesn't remount the
  // whole RevealScreen.
  onShowPack,
  onShip,
  hasNextSealedPack = false,
  onAdvanceToNextSealed,
  onDone,
  onLoopActiveChange,
  instant = false,
  volatility,
  cardSlot = 'card',
  demo,
}: WinnerStepProps) {
  const wallet = useWallet();
  const { requestTopUp, isStripeFunded } = usePayments();
  const { depositUrl } = useOperatorLinks();
  const pedestalSrc = pedestalSrcFor(offer);

  // Pool's real possible-drop items — prefetched + preloaded for the reel.
  // TEMPORARILY DISABLED: the reel spins the sealed PACK art (pack-variant
  // tiles, no pedestal — matching the first-open ReadyStep lane) instead of
  // the pool's item cards. Keep this feed — the item-card reel is coming
  // back later.
  // const nextPackItems = useNextPackItems(client, offer.id);
  // const nextPackImages = useMemo(
  //   () =>
  //     nextPackItems
  //       .map((i) => i.imageUrl)
  //       .filter((u): u is string => typeof u === 'string' && u.length > 0),
  //   [nextPackItems],
  // );

  // Pack art for the reel — every spinning tile shows the pack.
  const packReelImages = useMemo(
    () => (offer.packImageUrl ? [offer.packImageUrl] : []),
    [offer.packImageUrl],
  );

  // Warm the same CDN-resized pack art the pack-variant tiles request. The
  // first-open lane (SlotTile) asks for identical params, so this is usually
  // already cached — a no-op beyond the first open.
  useEffect(() => {
    const resolved = compressedImageUrl({
      url: offer.packImageUrl,
      ...PACK_REEL_IMG,
    });
    if (!resolved) return;
    const pre = new window.Image();
    pre.src = resolved;
  }, [offer.packImageUrl]);

  // Create Shipping CTA opens this modal — reviews fees & countdown.
  const [showShippingModal, setShowShippingModal] = useState(false);
  const disableShipping = useShippingDisabled();
  const isSleek = useIsSleek();

  // ── Episode + shuffle state ───────────────────────────────────────
  // `loopItem` null → first episode (the real pulled pack, sold via the
  // reveal machine's `onSell`). After a shuffle lands it holds the next
  // pack's real opened item (`loopOrderId` is its order, for the sell).
  // `epoch` keys WinnerContent so each new episode remounts + replays.
  const [loopItem, setLoopItem] = useState<IframePoolOrderItem | null>(null);
  const [loopOrderId, setLoopOrderId] = useState<string | null>(null);
  const [epoch, setEpoch] = useState(0);
  const [localSellState, setLocalSellState] = useState<SellState>('idle');

  // `opener` = a looped pack opened by the operator's library mechanic
  // (slice / burst) instead of the reel.
  const [phase, setPhase] = useState<'winner' | 'exiting' | 'shuffle' | 'opener'>('winner');

  // ── Loop packs on the library mechanic ─────────────────────────────
  // Same scene as the first open; the purchase (which also opens the pack
  // server-side) runs while the player cuts. A scene that cannot run here
  // hands the loop back to the reel. Once the ceremony is over the scene
  // stays underneath the episode's winner chrome (`loopScene`), exactly like
  // the first pull — the card never moves to a pedestal.
  const animation = useOpeningAnimation(offer.animation);
  const [openerFailed, setOpenerFailed] = useState(false);
  // The player's skip: a library pack in the loop opens with no scene — the
  // episode swaps the moment the purchase is in. Switched on mid-ceremony it
  // takes the running scene down the same way. The slot lane's reel keeps
  // spinning until the purchase is known (it must not stop on a placeholder)
  // and fast-forwards to the landing instead (`skip` prop below)
  const skipping = useSkipAnimation();
  const loopOpener = isOpenerVariant(animation.variant) && !openerFailed;
  const loopInstant = isOpenerVariant(animation.variant) && skipping;
  const [loopScene, setLoopScene] = useState(false);
  // Keys the loop scene: bumped when a new episode starts, never mid-episode
  const [openerEpoch, setOpenerEpoch] = useState(0);
  const [openerDone, setOpenerDone] = useState(false);
  // The purchase already opens the pack — nothing to fetch on the cut. The
  // carousel asks at the commit and waits for the answer before its second
  // tap, so this settles with the purchase: its outcome, when it is known
  const outcomeWaiters = useRef<((ok: boolean) => void)[]>([]);
  const loopOpenerOpened = useCallback(() => {
    const known = nextOutcomeRef.current;
    if (known) return Promise.resolve(known.kind === 'success');
    return new Promise<boolean>((resolve) => outcomeWaiters.current.push(resolve));
  }, []);
  const loopOpenerRevealed = useCallback(() => {
    setOpenerDone(true);
    return true;
  }, []);
  const loopOpenerError = useCallback(() => {
    setOpenerFailed(true);
    setPhase('shuffle');
  }, []);
  // Random pool item — the reel's spinning placeholder image only (no fallback card; failures abort to the error panel).
  // Unused while the reel spins pack art; restore with the item-card feed.
  // const [targetItem, setTargetItem] = useState<IframePoolItem | null>(null);
  // The real next pull (purchased + opened) the reel must land on.
  const [nextRealItem, setNextRealItem] = useState<IframePoolOrderItem | null>(
    null,
  );
  const [nextOrderId, setNextOrderId] = useState<string | null>(null);
  // Typed resolution of the parallel buy (null = still in flight). Drives the
  // reel's 'pending' | 'success' | 'failed' and the error copy.
  const [nextOutcome, setNextOutcome] = useState<NextPackOutcome | null>(null);
  const nextOutcomeRef = useRef(nextOutcome);
  nextOutcomeRef.current = nextOutcome;
  useEffect(() => {
    if (!nextOutcome) return;
    const waiters = outcomeWaiters.current;
    outcomeWaiters.current = [];
    for (const resolve of waiters) resolve(nextOutcome.kind === 'success');
  }, [nextOutcome]);
  // Bumped on retry so the reel remounts and spins fresh.
  const [shuffleEpoch, setShuffleEpoch] = useState(0);
  const buyingRef = useRef(false);

  // ── Loop pack-opening choreography ────────────────────────────────
  // Replays the first-open reveal after the reel lands on the pack:
  // white glow plate + rotating rays over the landed pack → plate expands
  // into a full-screen wipe → (Rare+ badge text sequence) → the next
  // episode mounts BEHIND the plate while it fades out. Mirrors
  // useRevealMachine's reveal path — no API call here, the pack was
  // already opened server-side while the reel was spinning.
  const [loopReveal, setLoopReveal] = useState<
    'glow' | 'expand' | 'badge' | 'fade' | null
  >(null);
  const [loopRays, setLoopRays] = useState(false);
  const loopRevealTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (loopRevealTimerRef.current !== null) {
        window.clearTimeout(loopRevealTimerRef.current);
      }
    },
    [],
  );

  // Pending 'exiting' → 'shuffle' handoff from restartReelSpin. Must be
  // cancellable: an INSUFFICIENT_BALANCE response can land before it fires
  // and send the phase back to 'winner' — without clearing this, the stale
  // timeout still flips it to 'shuffle' a moment later, spinning the reel
  // behind the deposit modal.
  const shuffleEnterTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (shuffleEnterTimerRef.current !== null) {
        window.clearTimeout(shuffleEnterTimerRef.current);
      }
    },
    [],
  );

  const resultHoldTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (resultHoldTimerRef.current !== null) {
        window.clearTimeout(resultHoldTimerRef.current);
      }
    },
    [],
  );

  const isLoop = loopItem !== null;
  const displayItem = loopItem ?? item;
  // Buyback offer of the CURRENT episode (first pull or a looped pack) —
  // drives both the Create Shipping link and the shipping modal so they
  // always act on the card actually on screen.
  const currentOffer = displayItem.bestActiveOffer;
  const effSellState = isLoop ? localSellState : sellState;

  // Lock host pack-navigation while a loop/shuffle is live — an arrow tap
  // then would remount us and tear down the in-flight Open Next Pack.
  useEffect(() => {
    onLoopActiveChange?.(isLoop || phase !== 'winner');
  }, [isLoop, phase, onLoopActiveChange]);

  const acceptBuyback = () => {
    // Nothing was bought, so nothing can be sold back.
    if (demo) return;
    if (!isLoop) {
      // First episode — the reveal machine owns the real sell. Sleek: the
      // panel's caption says where the money went, so mid-order there is no
      // toast; on the order's last pack the panel holds the result, then
      // the reveal leaves (`finishAfterResult`).
      if (isSleek) onSell(undefined, { notify: !willAdvanceToSealed });
      else onSell();
      return;
    }
    // Looped episodes always carry a real order id now (handleLanded only
    // advances on a successful purchase), so the sell is always real.
    if (!loopOrderId || !loopItem) return;
    setLocalSellState('selling');
    void sellPoolItem(client, loopOrderId, loopItem.id)
      .then(() => {
        setLocalSellState('sold');
        const gross = loopItem.bestActiveOffer?.grossPrice ?? 0;
        gameEvents.itemSold(loopOrderId, String(loopItem.id), gross);
        void wallet.refresh({ expectCredit: true });
        notifyBuyback(gross);
        winHaptic(gross);
      })
      .catch((err) => {
        toast.error(err instanceof Error ? err.message : 'Failed to sell');
        setLocalSellState('idle');
      });
  };

  // Resets the reel state and (re)plays the exit → shuffle entrance beats.
  // Shared by the initial shuffle kick-off and by the INSUFFICIENT_BALANCE
  // retry below, which needs a fresh spin after the deposit modal closes.
  const restartReelSpin = () => {
    // Item-card reel (disabled): a random pool card seeded the pending
    // landing tile. The pack-art reel lands on the pack itself.
    // const pick =
    //   nextPackItems.length > 0
    //     ? nextPackItems[Math.floor(Math.random() * nextPackItems.length)]!
    //     : null;
    // setTargetItem(pick);
    setNextRealItem(null);
    setNextOrderId(null);
    setNextOutcome(null);
    setLoopReveal(null);
    setLoopRays(false);
    // A new episode gets a fresh scene (new key) — but only at the handoff
    // below, not here: bumping the key at press time tore the current scene
    // (and the card standing in it) off screen in one frame while the chrome
    // was still flying out, which read as a hard cut back to the start.
    setOpenerDone(false);
    // Title + bottom fly out while the reel fades in OVER them (no longer
    // waiting for the full exit) — kills the momentum sag right at the
    // "I pressed go" moment.
    setPhase('exiting');
    if (shuffleEnterTimerRef.current !== null) {
      window.clearTimeout(shuffleEnterTimerRef.current);
    }
    shuffleEnterTimerRef.current = window.setTimeout(() => {
      shuffleEnterTimerRef.current = null;
      // Fresh scene for the new episode; the outgoing one cross-fades out
      // against it (see the scene block below).
      setOpenerEpoch((e) => e + 1);
      setPhase(loopOpener ? 'opener' : 'shuffle');
    }, 250);
  };

  // Cancels a pending/in-progress shuffle entrance and drops back to the
  // winner card — used when INSUFFICIENT_BALANCE interrupts the buy. Must
  // clear the 'exiting' → 'shuffle' timer above; otherwise it still fires
  // afterwards and spins the reel behind the deposit modal.
  const cancelReelSpin = () => {
    if (shuffleEnterTimerRef.current !== null) {
      window.clearTimeout(shuffleEnterTimerRef.current);
      shuffleEnterTimerRef.current = null;
    }
    // Back to the current episode without a new pull: its scene left with the
    // chrome, so the card returns on a pedestal
    setLoopScene(false);
    setPhase('winner');
  };

  // Buy + open the next pack in place while the reel spins. NEVER throws (the
  // resolver maps every failure to a typed outcome); we record that outcome so
  // the reel either lands (success) or aborts to the error panel (failure).
  // INSUFFICIENT_BALANCE here is the RACE fallback only — startNextPackShuffle
  // pre-checks the balance and opens the deposit before the reel ever spins, so
  // this branch is reached only when the balance went stale between that check
  // and the actual charge. It cancels the spinning reel, opens the deposit and,
  // if funded, retries the purchase ONCE.
  const buyAndOpenNext = async () => {
    if (buyingRef.current) return;
    buyingRef.current = true;
    try {
      // Demo loop: another free card, same landing/episode machinery. No
      // purchase, no wallet, and no host events for a round that isn't one.
      if (demo) {
        const outcome = await pullDemoNextPack(demo.pullCard);
        if (outcome.kind === 'success') {
          setNextRealItem(outcome.item);
          setNextOrderId(outcome.orderId);
          preloadWinnerImage(outcome.item.imageUrl);
        }
        setNextOutcome(outcome);
        return;
      }
      trackBeginCheckout(offer, 1);
      const hooks = {
        onPurchaseInitiated: (id: string) => gameEvents.purchaseInitiated(id),
        onPurchaseCompleted: (id: string, items: { id: string; name: string; rarity: string }[]) =>
          gameEvents.purchaseCompleted(id, items),
        onItemRevealed: (id: string, itemId: string, title: string, fmv: number | null) =>
          gameEvents.itemRevealed(id, itemId, title, fmv),
        onWalletRefresh: () => void wallet.refresh(),
      };
      let outcome = await buyAndOpenNextPack(client, String(offer.id), hooks, volatility, variantTellsFacts(animation.variant));

      if (outcome.kind === 'pre_purchase_error' && outcome.code === 'INSUFFICIENT_BALANCE') {
        const nextPackPriceCents = Math.round(parseFloat(offer.price ?? '0') * 100);
        if (isStripeFunded) {
          // Funding happens at the player's own pace in the deposit modal —
          // that's an open-ended wait, not a "purchase in flight" beat, so
          // cancel the spinning reel back to the winner card instead of
          // holding/extending it behind the modal (it would eventually run
          // out of spin runway and visibly freeze there).
          cancelReelSpin();
        }
        const funded = await requestTopUp(nextPackPriceCents);
        if (funded) {
          if (isStripeFunded) restartReelSpin();
          outcome = await buyAndOpenNextPack(client, String(offer.id), hooks, volatility, variantTellsFacts(animation.variant));
        } else if (isStripeFunded) {
          // The deposit screen already explained why the purchase can't go
          // through; the player closed it without paying, so don't repeat
          // the message in the reel's error panel — just stay on the winner card.
          return;
        }
      }

      if (outcome.kind === 'success') {
        setNextRealItem(outcome.item);
        setNextOrderId(outcome.orderId);
        // Warm the winner card now — by the time the reel settles and clears
        // (~800 ms) the image is already decoded in cache.
        preloadWinnerImage(outcome.item.imageUrl);
      } else if (outcome.kind === 'pre_purchase_error') {
        // Money was NOT taken — tell the operator the purchase failed.
        gameEvents.purchaseFailed(outcome.orderId, outcome.message, outcome.code);
        // The host answers it with its own top-up popup: stop the reel on the
        // winner card instead of landing on our error panel under it.
        if (isLowBalanceLeftToHost(outcome.code)) {
          cancelReelSpin();
          return;
        }
      }
      // post_purchase_error: purchaseCompleted already emitted; do NOT emit
      // purchaseFailed (the purchase succeeded) and do NOT fake a card.
      setNextOutcome(outcome);
    } finally {
      buyingRef.current = false;
    }
  };

  const startNextPackShuffle = () => {
    // A free rip has no price: straight to the ceremony, no balance check
    // and no top-up path.
    if (demo) {
      beginNextPackShuffle();
      return;
    }
    // Best-effort gate — skip the shuffle entirely instead of spinning the reel
    // just to land on an error (or, for Stripe, behind the deposit modal). The
    // cached balance goes stale easily (we only refresh on init/purchase/sell),
    // so a low one is confirmed against a fresh fetch before we refuse — a
    // player who just topped up must not be turned away. Backend stays the real
    // authority either way (the buy still re-checks server-side).
    if (canAffordOffer(offer.price, 1, wallet.balance_cents)) {
      beginNextPackShuffle();
      return;
    }
    void wallet.refresh().then((fresh) => {
      if (canAffordOffer(offer.price, 1, fresh)) {
        beginNextPackShuffle();
        return;
      }
      // Still can't afford it. Stripe operators top up in-iframe: open the
      // deposit modal DIRECTLY (never behind a spinning reel) and only start
      // the shuffle once funded. Seamless operators have no in-iframe top-up,
      // so fast-fail with the message.
      if (!isStripeFunded) {
        // Never reaches the server — emit here so the parent hears about it.
        gameEvents.purchaseFailed(null, INSUFFICIENT_BALANCE_MESSAGE, 'INSUFFICIENT_BALANCE');
        if (isLowBalanceLeftToHost('INSUFFICIENT_BALANCE')) return;
        toast.error(
          INSUFFICIENT_BALANCE_MESSAGE,
          depositUrl
            ? { action: { label: 'Add funds', onClick: () => openDepositUrl(depositUrl) } }
            : undefined,
        );
        return;
      }
      const nextPackPriceCents = Math.round(parseFloat(offer.price ?? '0') * 100);
      void requestTopUp(nextPackPriceCents).then((funded) => {
        if (funded) beginNextPackShuffle();
      });
    });
  };

  const beginNextPackShuffle = () => {
    restartReelSpin();
    void buyAndOpenNext();
  };

  // Primary forward CTA. While the purchase still has a sealed pack ahead,
  // advance to it (open what you already bought); only once every purchased
  // pack is opened does it buy a brand-new pack via the shuffle loop.
  const willAdvanceToSealed =
    hasNextSealedPack && !isLoop && Boolean(onAdvanceToNextSealed);
  const willFinish = !demo && !willAdvanceToSealed && Boolean(onDone);
  // Sleek resolves the order's last pack straight out of the reveal.
  const sleekFinishes = isSleek && willFinish;
  const handleVault = () => {
    if (!sleekFinishes || !onDone) return;
    notifyVaulted(1, displayItem.imageUrl, 'vault');
    // Without an offer the panel shows no "Vaulted" result to hold on.
    if (!currentOffer) onDone();
  };
  const finishAfterResult = () => {
    if (!onDone || resultHoldTimerRef.current !== null) return;
    // The balance refreshed while the result held, so the lobby's pill would
    // mount on the new figure — have it play the credit when it shows.
    if (effSellState === 'sold') wallet.queueCreditFlash();
    resultHoldTimerRef.current = window.setTimeout(onDone, SLEEK_RESULT_HOLD_MS);
  };
  const handleOpenNextPack = () => {
    if (willAdvanceToSealed && onAdvanceToNextSealed) {
      onAdvanceToNextSealed();
      return;
    }
    if (willFinish && onDone) {
      onDone();
      return;
    }
    startNextPackShuffle();
  };

  // CTA copy must match what the tap actually does: advancing to an
  // already-paid sealed pack stays "Open Next Pack"; the shuffle loop BUYS
  // a fresh pack, so it says so with the offer price. `formatPrice` is ''
  // until the operator currency lands — drop the price tail then. Sleek
  // follows the partner mockup's "Buy another pack • $25".
  const nextPackPriceLabel = formatPrice(Number(offer.price));
  const buyAnotherCta = !nextPackPriceLabel
    ? 'Buy another pack'
    : isSleek
      ? `Buy another pack • ${nextPackPriceLabel}`
      : `Buy another pack for ${nextPackPriceLabel}`;
  const nextPackCta = demo
    ? demo.ctaLabel
    : willAdvanceToSealed
      ? isSleek
        ? 'Rip next pack'
        : 'Open Next Pack'
      : willFinish
        ? 'Done'
        : buyAnotherCta;

  // Swap to the next episode — the freshly pulled card replaces the reel
  // (which unmounts behind the expanded white plate). `viaScene`: the
  // library scene revealed it and stays underneath the winner chrome.
  const advanceEpisode = useCallback(
    (viaScene = false) => {
      if (nextOutcome?.kind !== 'success' || !nextRealItem) return;
      setLoopItem(nextRealItem);
      setLoopOrderId(nextOrderId);
      setLocalSellState('idle');
      setLoopScene(viaScene);
      setEpoch((e) => e + 1);
      setPhase('winner');
    },
    [nextOutcome, nextRealItem, nextOrderId],
  );

  // The scene's ceremony is over; the purchase may still be settling — the
  // episode advances the moment both are in. A skipped scene counts as over
  // before it begins: the episode advances on the purchase alone, onto a
  // pedestal (there is no scene to keep the card on).
  useEffect(() => {
    if (phase !== 'opener') return;
    if (nextOutcome?.kind !== 'success') return;
    if (!openerDone && !loopInstant) return;
    setOpenerDone(false);
    advanceEpisode(openerDone && !loopInstant);
  }, [phase, openerDone, loopInstant, nextOutcome, advanceEpisode]);

  // A purchase that failed under the scene: the reel owns the error panel
  // (Try Again / Back / Add funds), so it takes the stage over.
  useEffect(() => {
    if (phase === 'opener' && nextOutcome && nextOutcome.kind !== 'success') {
      setPhase('shuffle');
    }
  }, [phase, nextOutcome]);

  // Badge of the pack the reel just landed on — drives the glow label,
  // the rarity gate and the character/grade text sequence.
  const loopBadge = nextRealItem?.badge ?? null;
  const loopRevealBadge =
    loopBadge && isRarePlus(loopBadge.name) ? loopBadge.name : null;
  // The carousel's story for the looped pull, once it is known
  const loopFacts = useMemo(
    () => (nextRealItem ? cardFactsFor(nextRealItem) : undefined),
    [nextRealItem],
  );

  // Success only — the reel calls this after landing on the pack.
  // Pre-choreography behaviour (kept — we'll likely want the instant
  // hand-off again later): the landing advanced the episode immediately,
  // with no glow reveal in between.
  // const handleLanded = () => {
  //   if (nextOutcome?.kind !== 'success' || !nextRealItem) return;
  //   setLoopItem(nextRealItem);
  //   setLoopOrderId(nextOrderId);
  //   setLocalSellState('idle');
  //   setEpoch((e) => e + 1);
  //   setPhase('winner');
  // };
  const handleLanded = (reelSkipped: boolean) => {
    if (nextOutcome?.kind !== 'success' || !nextRealItem) return;
    // Skip mirrors the first open: straight to the winner, no glow.
    if (reelSkipped) {
      advanceEpisode();
      return;
    }
    setLoopReveal('glow');
    setLoopRays(true);
    // Hold the steady plate + rays for a beat (same EXPAND_DELAY_MS the
    // first open uses), then swell the plate into the full-screen wipe.
    loopRevealTimerRef.current = window.setTimeout(() => {
      setLoopReveal('expand');
    }, EXPAND_DELAY_MS);
  };

  // Plate finished its swell — drop the rays and either play the badge
  // text sequence (Rare+ with character/grade, like the first open) or
  // advance right away and let the plate fade over the new episode.
  const handleGlowExpanded = () => {
    setLoopRays(false);
    if (loopRevealBadge && loopBadge && (loopBadge.character || loopBadge.grade)) {
      // Same short breath the first open takes before the text plays.
      loopRevealTimerRef.current = window.setTimeout(
        () => setLoopReveal('badge'),
        180,
      );
    } else {
      advanceEpisode();
      setLoopReveal('fade');
    }
  };

  const handleBadgeSequenceDone = () => {
    advanceEpisode();
    setLoopReveal('fade');
  };

  // Map the local stage onto the glow overlay's phase prop.
  const loopGlowPhase: GlowPhase | null =
    loopReveal === 'glow'
      ? 'reveal'
      : loopReveal === 'expand' || loopReveal === 'badge'
        ? 'expand'
        : loopReveal;

  // "Try Again" — pre-purchase failures only (canRetry). Remount the reel
  // (fresh spin) and re-run the full buy. Never reached for post-purchase
  // failures (those hide the retry button, so no double charge is possible).
  const handleRetryNextPack = () => {
    // Item-card reel (disabled): a random pool card seeded the pending
    // landing tile. The pack-art reel lands on the pack itself.
    // const pick =
    //   nextPackItems.length > 0
    //     ? nextPackItems[Math.floor(Math.random() * nextPackItems.length)]!
    //     : null;
    // setTargetItem(pick);
    setNextRealItem(null);
    setNextOrderId(null);
    setNextOutcome(null);
    setShuffleEpoch((e) => e + 1);
    setOpenerDone(false);
    setOpenerEpoch((e) => e + 1);
    void buyAndOpenNext();
  };

  // "Back" from the error panel. A post-purchase failure means the pack is
  // paid + opened server-side, so send the user to the pack page to find it;
  // a pre-purchase failure just returns to the card they already have.
  const handleAbortBack = () => {
    const wasPaid = nextOutcome?.kind === 'post_purchase_error';
    setNextOutcome(null);
    if (wasPaid) {
      onShowPack();
    } else {
      // Same as cancelReelSpin: the episode's scene is gone, pedestal card
      setLoopScene(false);
      setPhase('winner');
    }
  };

  // Pack-art reel: the wheel spins AND lands on the sealed pack — like the
  // first open — and the real pull is then revealed by the next winner
  // episode. The landing is still gated on the purchase `outcome`.
  // Item-card reel (disabled): landed on the real pull once it was known.
  // const targetImage =
  //   nextRealItem?.imageUrl ?? targetItem?.imageUrl ?? displayItem.imageUrl;
  const targetImage = offer.packImageUrl ?? displayItem.imageUrl;

  // Map the typed outcome down to the reel's coarse signal + error copy.
  const reelOutcome: 'pending' | 'success' | 'failed' =
    nextOutcome == null
      ? 'pending'
      : nextOutcome.kind === 'success'
        ? 'success'
        : 'failed';
  const reelError =
    nextOutcome && nextOutcome.kind !== 'success'
      ? nextPackErrorCopy(nextOutcome)
      : null;
  // Seamless + casino deposit_url: the stale-balance race lands here with
  // INSUFFICIENT_BALANCE — offer the cashier instead of a bare Try Again.
  const nextAddFunds =
    depositUrl &&
    nextOutcome?.kind === 'pre_purchase_error' &&
    nextOutcome.code === 'INSUFFICIENT_BALANCE'
      ? () => openDepositUrl(depositUrl)
      : undefined;

  return (
    <>
      {/* Looped pack on the library mechanic. Mounted from the moment the
          player buys until the NEXT episode starts, so the card the scene
          settled stays put while this episode's chrome arrives above it
          (later in the DOM, same z level). */}
      {((phase === 'opener' && !loopInstant) ||
        ((phase === 'winner' || phase === 'exiting') && loopScene)) && (
        // The episode swap is a cross-fade instead of a cut: the scene the
        // player is looking at dims out while the chrome flies away (it used
        // to vanish the frame the CTA was pressed), and the fresh sealed pack
        // fades up in its place. Unmounting stays immediate — a scene handing
        // the stage to the reel's error panel must not linger over it.
        <motion.div
          key={`opener-scene-${openerEpoch}`}
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase === 'exiting' ? 0 : 1 }}
          transition={{
            duration: phase === 'exiting' ? 0.25 : 0.34,
            ease: 'easeOut',
          }}
        >
          <OpenerReadyStep
            key={`opener-${openerEpoch}`}
            offer={offer}
            animation={animation}
            cardImageUrl={nextRealItem?.imageUrl ?? null}
            badge={loopRevealBadge}
            {...(loopFacts ? { facts: loopFacts } : {})}
            onOpened={loopOpenerOpened}
            onRevealed={loopOpenerRevealed}
            onError={loopOpenerError}
          />
        </motion.div>
      )}
      <AnimatePresence>
        {phase === 'shuffle' ? (
          <CardShuffleReel
            key={`reel-${shuffleEpoch}`}
            // Item-card reel (disabled): images={nextPackImages}
            // currentImage={displayItem.imageUrl}
            images={packReelImages}
            currentImage={offer.packImageUrl ?? displayItem.imageUrl}
            targetImage={targetImage}
            tileVariant="pack"
            revealing={loopReveal !== null}
            skip={skipping}
            pedestalSrc={pedestalSrc}
            outcome={reelOutcome}
            error={reelError}
            onLanded={handleLanded}
            onAddFunds={nextAddFunds}
            onRetry={handleRetryNextPack}
            onBack={handleAbortBack}
          />
        ) : phase === 'opener' ? null : (
          <WinnerContent
            key={`ep-${epoch}`}
            item={displayItem}
            pedestalSrc={pedestalSrc}
            bestOffer={displayItem.bestActiveOffer}
            sellState={effSellState}
            instant={isLoop ? false : instant}
            // Post-glow episodes replay the SAME full reveal cascade as a
            // fresh pull — title drops in first, THEN the card lifts in.
            // Pre-glow behaviour (kept for later): quickHeader={isLoop}
            // painted title + card together and only cascaded the bottom.
            quickHeader={false}
            leaving={phase === 'exiting'}
            onAcceptBuyback={acceptBuyback}
            onOpenNextPack={handleOpenNextPack}
            nextPackCta={nextPackCta}
            nextPackIsDone={willFinish}
            onVault={handleVault}
            {...(sleekFinishes ? { onResultShown: finishAfterResult } : {})}
            onCreateShipping={() => setShowShippingModal(true)}
            // "Keep & ship" link shown whenever the current card still has a
            // live buyback offer — i.e. the offer phase, on the first pull AND
            // looped packs. Hidden in the no-offer/returning state (the
            // shipping modal needs the offer to price the fallback).
            showCreateShipping={currentOffer != null && !disableShipping}
            cardSlot={isLoop ? (loopScene ? 'scene' : 'card') : cardSlot}
            demo={Boolean(demo)}
            onPullForReal={onShowPack}
          />
        )}
      </AnimatePresence>

      {/* Loop pack-opening overlays. Rays sit BELOW the reel (z-30 vs the
          reel's z-40) so the burst reads behind the landed pack, exactly
          like the first open where the rays live behind the lane; the glow
          plate + badge text ride ABOVE it (z-50). Fixed wrappers anchor to
          the reveal overlay host (its @container makes it the containing
          block for fixed descendants). */}
      {loopRays && (
        <div className="pointer-events-none fixed inset-0 z-30">
          <RevealRays />
        </div>
      )}
      {loopGlowPhase !== null && (
        <div className="pointer-events-none fixed inset-0 z-50">
          <RevealGlowOverlay
            phase={loopGlowPhase}
            badge={loopRevealBadge}
            onExpandComplete={handleGlowExpanded}
            onFadeComplete={() => setLoopReveal(null)}
          />
          {loopReveal === 'badge' && loopBadge && (
            <BadgeRevealSequence
              character={loopBadge.character}
              grade={loopBadge.grade}
              onComplete={handleBadgeSequenceDone}
            />
          )}
        </div>
      )}

      {/* Shipping confirmation modal — opened from Create Shipping. Acts on
          the CURRENT episode's card (first pull or a looped pack). Anchored
          at orchestrator level so the modal frame spans the whole screen. */}
      {showShippingModal && currentOffer && (
        <RevealShippingModal
          client={client}
          item={displayItem}
          offer={currentOffer}
          onClose={() => setShowShippingModal(false)}
          onConfirm={() => {
            setShowShippingModal(false);
            if (onShip) onShip(displayItem);
            else onShowPack();
          }}
          onBuyback={() => {
            setShowShippingModal(false);
            acceptBuyback();
          }}
        />
      )}
    </>
  );
}
